import { BarChart3 } from "lucide-react";
import { Link } from "wouter";
import { PUBLIC_BUSINESS } from "../lib/public-business";

const smsOptinImages = import.meta.glob("../../public/sms-optin.png", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>;
const smsOptinImage = Object.values(smsOptinImages)[0];

export default function SmsAlertsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3" aria-label="DealerMind">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BarChart3 className="h-5 w-5" aria-hidden="true" />
            </div>
            <span className="text-lg font-bold tracking-tight">DealerMind</span>
          </div>
          <Link
            href="/login"
            className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="link-log-in"
          >
            Log in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6">
        <section className="border-b py-12 sm:py-16">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-primary">
            Messaging
          </p>
          <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-5xl sm:leading-[1.12]">
            DealerMind SMS Alerts
          </h1>
        </section>

        <section className="grid gap-6 border-b py-10 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-12">
          <h2 className="text-2xl font-semibold tracking-tight">What it is</h2>
          <p className="leading-7 text-muted-foreground">
            DealerMind SMS Alerts is a text-message alert program run by DealerMind Intelligence
            LLC for dealership staff who use DealerHub, DealerMind&apos;s dealership app. It sends
            new-lead alerts, alerts when a shopper asks for a person or a test drive, and account
            alerts such as a calendar connection that stopped syncing. It never texts car shoppers
            and never sends marketing.
          </p>
        </section>

        <section className="grid gap-6 border-b py-10 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-12">
          <h2 className="text-2xl font-semibold tracking-tight">Who receives texts</h2>
          <p className="leading-7 text-muted-foreground">
            Only dealership employees who are DealerHub users, or staff they add with that
            person&apos;s permission. Car shoppers are never enrolled in this program.
          </p>
        </section>

        <section className="grid gap-6 border-b py-10 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-12">
          <h2 className="text-2xl font-semibold tracking-tight">How to opt in</h2>
          <div>
            <p className="leading-7 text-muted-foreground">
              A dealership user signs in to DealerHub, opens Settings → Notifications, enters up
              to three staff mobile numbers, and checks the box below. Texts start only after the
              box is checked and SMS alerts are turned on. Each number then gets a confirmation
              text.
            </p>

            <fieldset
              disabled
              className="mt-6 rounded-lg border border-border bg-muted/40 p-5 sm:p-6"
              aria-label="Static example of the DealerHub SMS opt-in"
            >
              <legend className="px-2 text-sm font-semibold">SMS alert opt-in</legend>
              <label htmlFor="sms-alert-phone" className="mb-2 block text-sm font-medium">
                Staff mobile number
              </label>
              <input
                id="sms-alert-phone"
                type="tel"
                value="813-555-1212"
                disabled
                readOnly
                className="w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-80"
              />
              <div className="mt-4 flex items-start gap-3">
                <input
                  id="sms-alert-consent"
                  type="checkbox"
                  disabled
                  className="mt-1 h-4 w-4 shrink-0 accent-primary disabled:cursor-not-allowed"
                />
                <label
                  htmlFor="sms-alert-consent"
                  className="text-sm leading-6 text-muted-foreground"
                >
                  I agree to receive SMS lead and account alerts from DealerMind (DealerHub) at
                  the number(s) above, and I confirm each number belongs to me or to a staff
                  member who asked to receive them. Message frequency varies. Message and data
                  rates may apply. Reply HELP for help, STOP to opt out. See our{" "}
                  <Link
                    href="/privacy"
                    className="underline underline-offset-4 hover:text-foreground"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="/terms"
                    className="underline underline-offset-4 hover:text-foreground"
                  >
                    Terms
                  </Link>
                  .
                </label>
              </div>
            </fieldset>
            <p className="mt-3 text-sm text-muted-foreground">
              This is what the opt-in looks like inside DealerHub (sign-in required).
            </p>
            {smsOptinImage ? (
              <img
                src={smsOptinImage}
                alt="DealerHub Settings → Notifications SMS opt-in"
                className="mt-6 h-auto max-w-full rounded-lg border border-border"
              />
            ) : null}
          </div>
        </section>

        <section className="grid gap-6 border-b py-10 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-12">
          <h2 className="text-2xl font-semibold tracking-tight">Sample messages</h2>
          <div className="space-y-4">
            <p className="rounded-lg border border-border bg-muted/40 p-4 font-mono text-sm leading-6 text-muted-foreground">
              DealerHub: New lead for Sunshine Motors. Customer: Jane D. Vehicle: 2021 Honda
              Civic. Source: Website chat. Log in for details. Reply STOP to opt out.
            </p>
            <p className="rounded-lg border border-border bg-muted/40 p-4 font-mono text-sm leading-6 text-muted-foreground">
              DealerHub by DealerMind: You&apos;re signed up for lead alerts for Sunshine Motors.
              Msg frequency varies. Msg &amp; data rates may apply. Reply HELP for help, STOP to
              opt out.
            </p>
          </div>
        </section>

        <section className="grid gap-6 border-b py-10 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-12">
          <h2 className="text-2xl font-semibold tracking-tight">
            Frequency/cost/help/opt out
          </h2>
          <p className="leading-7 text-muted-foreground">
            Message frequency varies with the dealership&apos;s lead activity. Message and data
            rates may apply. Reply STOP to any message to stop; you&apos;ll get one confirmation.
            Reply HELP for help, or email{" "}
            <a
              href={`mailto:${PUBLIC_BUSINESS.email}`}
              className="underline underline-offset-4 hover:text-foreground"
            >
              {PUBLIC_BUSINESS.email}
            </a>
            . You can also remove your number in DealerHub settings. Wireless carriers are not
            liable for delayed or undelivered messages.
          </p>
        </section>

        <section className="grid gap-6 border-b py-10 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-12">
          <h2 className="text-2xl font-semibold tracking-tight">Privacy</h2>
          <p className="leading-7 text-muted-foreground">
            Mobile numbers and SMS consent information will not be shared with third parties or
            affiliates for marketing or promotional purposes.
          </p>
        </section>

        <section className="grid gap-6 py-10 sm:grid-cols-[15rem_1fr] sm:gap-12 sm:py-12">
          <h2 className="text-2xl font-semibold tracking-tight">Contact</h2>
          <address className="not-italic text-sm leading-7 text-muted-foreground">
            <p className="font-semibold text-foreground">{PUBLIC_BUSINESS.name}</p>
            <p>
              Email:{" "}
              <a
                className="underline underline-offset-4 hover:text-foreground"
                href={`mailto:${PUBLIC_BUSINESS.email}`}
              >
                {PUBLIC_BUSINESS.email}
              </a>
            </p>
            <p>Mailing Address: {PUBLIC_BUSINESS.mailingAddress}</p>
            <p>Phone: {PUBLIC_BUSINESS.phone}</p>
          </address>
        </section>
      </main>

      <footer className="border-t">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 py-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span>&copy; 2026 DealerMind. All rights reserved.</span>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label="Legal">
            <Link href="/sms-alerts" className="transition-colors hover:text-foreground">
              SMS Alerts
            </Link>
            <Link href="/terms" className="transition-colors hover:text-foreground">
              Terms of Service
            </Link>
            <Link href="/privacy" className="transition-colors hover:text-foreground">
              Privacy Policy
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
